import { FC, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	IconButton,
	Radio,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { deleteMethodRequest, setDefaultRequest } from 'redux/reducers/paymentMethods/reducer';
import {
	getPaymentMethodActionLoading,
	getPaymentMethodsBlockedMessage,
	getPaymentMethodsList,
	getPaymentMethodsListLoading,
} from 'redux/reducers/paymentMethods/selectors';
import {
	DEFAULT_CAPABLE_TYPES,
	formatExpiry,
	getMethodStatusLabel,
	METHOD_TYPE_LABELS,
	STATUS_LABELS,
	TMethodStatusLabel,
} from 'redux/reducers/paymentMethods/constants';
import { TPaymentMethod } from 'services/api/paymentMethods/types';
import { EasChip } from 'ui/EasChip';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { notificationContainer } from 'utils/notificationContainer';
import { PaymentMethodIcon } from '../BrandIcons';
import { BlockedTooltip } from '../ClientAccess';
import { PaymentMethodServicesDialog } from '../PaymentMethodServicesDialog';
import { EasTableValue } from 'ui/EasTable/EasTableValue';
import { ETableStructureType } from 'services/constants/tableStructureTypes';
import { Delete, Edit } from '@mui/icons-material';
import { EasTooltip } from 'ui/EasTooltip';
import { COLORS } from 'services/constants/colors';

const STATUS_COLORS: Record<
	TMethodStatusLabel,
	'success.light' | 'warning.light' | 'error.light' | 'default.light' | 'info.light'
> = {
	[STATUS_LABELS.ACTIVE]: 'success.light',
	[STATUS_LABELS.EXPIRING_SOON]: 'warning.light',
	[STATUS_LABELS.EXPIRED]: 'error.light',
	[STATUS_LABELS.PENDING]: 'info.light',
	[STATUS_LABELS.DISABLED]: 'default.light',
	[STATUS_LABELS.FAILED]: 'error.light',
};

const canBeDefault = (method: TPaymentMethod) =>
	DEFAULT_CAPABLE_TYPES.includes(method.type) && method.status === 'active' && !method.isExpired;

const cellSx = { padding: '8px', fontSize: '.875rem' };

export const PaymentMethodsTable: FC = () => {
	const dispatch = useDispatch();
	const methods = useSelector(getPaymentMethodsList);
	const loading = useSelector(getPaymentMethodsListLoading);
	const actionLoading = useSelector(getPaymentMethodActionLoading);
	// changes blocked by the operator: the list stays visible, the actions are disabled
	const blockedMessage = useSelector(getPaymentMethodsBlockedMessage);

	const [methodToDelete, setMethodToDelete] = useState<TPaymentMethod | null>(null);
	const [methodToEdit, setMethodToEdit] = useState<TPaymentMethod | null>(null);

	const handleDelete = () => {
		// blocked while the confirmation was open
		if (blockedMessage) notificationContainer(blockedMessage, 'info');
		else if (methodToDelete) dispatch(deleteMethodRequest({ id: methodToDelete.id }));
		setMethodToDelete(null);
	};

	if (loading && !methods) {
		return (
			<Box>
				{[0, 1].map((row) => (
					<Skeleton key={row} variant="rectangular" height="30px" sx={{ mb: 2 }} />
				))}
			</Box>
		);
	}

	if (!methods?.length) {
		return (
			<Typography color="primary" sx={{ fontWeight: 'bold', my: 2 }}>
				No saved payment methods yet.
			</Typography>
		);
	}

	return (
		<>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={cellSx}>Type</TableCell>
							<TableCell sx={cellSx}>Details</TableCell>
							<TableCell sx={cellSx}>Status</TableCell>
							<TableCell sx={cellSx}>Services</TableCell>
							<TableCell sx={cellSx} align="center">
								Default (used first)
							</TableCell>
							<TableCell sx={cellSx} align="right"></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{methods.map((method) => {
							const statusLabel = getMethodStatusLabel(method);
							const expiry = formatExpiry(method);
							const inactive = method.status === 'disabled' || method.status === 'failed';
							return (
								<TableRow key={method.id} sx={{ opacity: inactive ? 0.6 : 1 }}>
									<TableCell sx={cellSx}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
											<PaymentMethodIcon
												type={method.type}
												cardBrand={method.cardBrand ?? method.brand}
												bankLogoUrl={method.bankLogoUrl}
												bankName={method.bankName}
											/>
											<span>{METHOD_TYPE_LABELS[method.type] ?? method.type}</span>
										</Box>
									</TableCell>
									<TableCell sx={cellSx}>
										<div>{method.label}</div>
										{expiry && (
											<Typography
												component="div"
												sx={{ fontSize: '.75rem', color: 'text.secondary' }}
											>
												Expires {expiry}
											</Typography>
										)}
										{method.type === 'bacs_direct_debit' && method.mandateReference && (
											<Typography
												component="div"
												sx={{ fontSize: '.75rem', color: 'text.secondary' }}
											>
												Mandate {method.mandateReference}
											</Typography>
										)}
									</TableCell>
									<TableCell sx={cellSx}>
										<EasChip
											size="small"
											label={statusLabel}
											bgcolor={STATUS_COLORS[statusLabel]}
										/>
									</TableCell>
									<TableCell sx={cellSx}>
										{method.allServices ? (
											<EasChip label="All services" />
										) : (
											<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
												{method.services.map((service) => (
													<EasChip key={service.id} label={service.name} />
												))}
											</Box>
										)}
									</TableCell>
									<TableCell sx={cellSx} align="center">
										{DEFAULT_CAPABLE_TYPES.includes(method.type) ? (
											<BlockedTooltip message={blockedMessage}>
												<Radio
													size="small"
													checked={method.isDefault}
													disabled={actionLoading || !canBeDefault(method) || !!blockedMessage}
													onChange={() => {
														if (!method.isDefault) dispatch(setDefaultRequest({ id: method.id }));
													}}
													inputProps={{ 'aria-label': `Use ${method.label} first` }}
												/>
											</BlockedTooltip>
										) : (
											<Tooltip title="Direct Debit is always used first for the services it covers">
												<Typography
													component="span"
													sx={{ fontSize: '1rem', color: COLORS.PRIMARY, fontWeight: 'bold' }}
												>
													—
												</Typography>
											</Tooltip>
										)}
									</TableCell>
									<TableCell sx={cellSx} align="right">
										<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
											{/* <BlockedTooltip message={blockedMessage}>
												<Button
													size="small"
													variant="contained"
													disabled={actionLoading || inactive || !!blockedMessage}
													onClick={() => setMethodToEdit(method)}
													sx={{ textTransform: 'none', borderRadius: '12px' }}
												>
													Edit services
												</Button>
											</BlockedTooltip> */}
											<EasTooltip title="Edit services">
												<IconButton
													aria-label="Edit services"
													onClick={() => setMethodToEdit(method)}
													disabled={actionLoading || inactive || !!blockedMessage}
												>
													<Edit color="primary" />
												</IconButton>
											</EasTooltip>
											<EasTooltip title="Delete">
												<IconButton
													aria-label="Delete"
													onClick={() => setMethodToDelete(method)}
													disabled={actionLoading || !!blockedMessage}
												>
													<Delete color="error" />
												</IconButton>
											</EasTooltip>
											{/* <BlockedTooltip message={blockedMessage}>
												<Button
													size="small"
													variant="outlined"
													color="error"
													disabled={actionLoading || !!blockedMessage}
													onClick={() => setMethodToDelete(method)}
													sx={{ textTransform: 'none', borderRadius: '12px' }}
												>
													Delete
												</Button>
											</BlockedTooltip> */}
										</Box>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>

			<ConfirmationDialog
				open={!!methodToDelete}
				setClose={() => setMethodToDelete(null)}
				handleConfirm={handleDelete}
				title="Delete payment method"
				confirmButtonText="Delete"
				cancelButtonText="Cancel"
			>
				<Typography>
					Are you sure you want to delete <b>{methodToDelete?.label}</b>? It will no longer be used
					for automatic payments.
				</Typography>
			</ConfirmationDialog>
			<PaymentMethodServicesDialog method={methodToEdit} onClose={() => setMethodToEdit(null)} />
		</>
	);
};
